<script setup>
import omit from 'lodash/omit'

const route = useRoute()
const user = useStrapiUser()
const companyStore = useCompanyStore()
const slideOverStore = useSlideOverStore()
const { getVacancies } = useApiVacancy()

await useAsyncData(`company-${route.params.slug}`, () =>
  companyStore.getCompany(route.params.slug),
)
const company = computed(() => companyStore.company)

if (!company.value) {
  navigateTo({ name: 'not-found' })
}

const { data: vacancies } = await useLazyAsyncData(
  `company-vacancies-${route.params.slug}`,
  getCompanyVacancies,
)

const isProfessional = computed(() => user.value?.type === 'professional')
const isOwner = computed(() => user.value?.company?.id === company.value?.id)
const matchingProfessional = computed(() => ({
  ...user.value.professional,
  user: omit(user.value, ['professional']),
}))

watch(
  () => route.query?.slideOver,
  () => {
    if (route.query.slideOver) {
      switch (route.query.slideOver) {
        case 'personalia':
          slideOverStore.openSlideover('CompaniesPersonalia')
          break
        case 'whyWorkWithUs':
          slideOverStore.openSlideover('CompaniesWhyWorkWithUs')
          break
        case 'about':
          slideOverStore.openSlideover('CompaniesAbout')
          break
        case 'media':
          slideOverStore.openSlideover('CompaniesMedia')
          break
      }
    }
  },
  { immediate: true },
)

async function getCompanyVacancies() {
  return (
    await getVacancies({
      pagination: {
        pageSize: 3,
        page: 1,
      },
      sort: ['match:desc'],
      filters: {
        $and: [
          { company: { slug: { $eq: route.params.slug } } },
          { status: { $eq: 'published' } },
        ],
      },
    })
  )?.data
}

const matchings = computed(() => {
  return Object.entries(company?.value)
    .filter(([key]) => key.startsWith('matchingscore_')) // Filter only matchingscore_ properties
    .map(([key, score], index) => {
      // Extract type from the key
      const type = key.replace('matchingscore_', '')

      return {
        id: index,
        score,
        type,
        identifier: `${type}-professional${user?.value?.professional?.id}-company${company?.value?.id}`,
        createdAt: null,
        updatedAt: null,
      }
    })
})
</script>

<template>
  <div v-if="company" class="bg-gray-50">
    <CompaniesDetailHero :company />
    <div class="h-[60px] w-full bg-primary lg:h-[100px]"></div>
    <BaseContainer class="mx-0 mt-[-100px] lg:mt-[-100px]">
      <div class="grid w-full grid-cols-1 gap-6 lg:grid-cols-3">
        <div class="col-span-2 flex flex-col gap-y-6 lg:gap-6">
          <CompanyProfessionalMatching
            v-if="isProfessional"
            :professional="matchingProfessional"
            :company
            :matchings="matchings"
          />
          <CompaniesDetailAboutUs :company :can-edit="isOwner" />
        </div>

        <div class="flex w-full flex-col gap-4">
          <CompaniesCard
            :item="company"
            :show-go-to-company="false"
            show-send-message
            show-socials
          />

          <CompaniesDetailPublicEmployees :company :can-edit="isOwner" />
        </div>
      </div>

      <div class="mt-16 flex flex-col gap-8 pb-16">
        <h2 class="font-heading text-4xl font-black uppercase text-secondary">
          Wie we zoeken als bedrijf
        </h2>
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div class="">
            <AssessmentLookingFor
              :company
              type="personality"
              class="min-h-[457px] w-full"
            />
          </div>

          <div class="">
            <AssessmentLookingFor
              :company
              type="motivations"
              class="min-h-[457px] w-full"
            />
          </div>

          <div class="">
            <AssessmentLookingFor
              :company
              type="culture"
              class="min-h-[457px] w-full"
            />
          </div>

          <div class="">
            <AssessmentLookingFor
              :company
              type="cognitive"
              class="min-h-[457px] w-full"
            />
          </div>
        </div>
      </div>
    </BaseContainer>
    <div class="w-full bg-white">
      <BaseContainer>
        <div class="flex flex-col gap-6 pb-20 pt-14">
          <h2 class="font-heading text-4xl font-black uppercase text-secondary">
            Openstaande vacatures
          </h2>
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <VacanciesCard
              v-for="vacancy in vacancies"
              :key="vacancy.id"
              :item="vacancy"
            />
          </div>
        </div>
      </BaseContainer>
    </div>
  </div>
</template>
